import {
  CallOutline,
  ChevronDown,
  ChevronForward,
  DocumentTextOutline,
  HelpCircleOutline,
  MailOutline,
  SearchOutline,
  ShieldCheckmarkOutline,
  SparklesOutline,
  TimeOutline,
  WalletOutline,
} from "@raresail/react-ionicons";
import { useState } from "react";
import { Link } from "react-router-dom";
import styled from "styled-components";
import {
  primary5,
  primary10,
  PRIMARY_COLOR,
  PRIMARY_LIGHT,
} from "../../../constants/colors";
import { alpha } from "@mui/material";
import { useTitle } from "../../../hooks/common/useTitle";
import { AnimateChildren, Heading } from "../../elements/Elements";

// ─── FAQ Data ───────────────────────────────────────────────────
const FAQ_CATEGORIES = [
  {
    id: "account",
    label: "Account & KYC",
    icon: ShieldCheckmarkOutline,
    color: "#1565c0",
    faqs: [
      {
        q: "How do I create an account?",
        a: "Download the app or visit our website, tap 'Sign Up', choose your role (Investor or Borrower), fill in your details including a valid phone number, and set a password. Your account is activated immediately.",
      },
      {
        q: "What documents do I need for KYC verification?",
        a: "You need a valid National ID, Passport, or Driver's License. You'll also need to provide your date of birth, NIN (National Identification Number), and complete your investment profile questionnaire. KYC review typically takes 1-2 business days.",
      },
      {
        q: "Can I change my account type (Investor/Borrower)?",
        a: "No. Your account type is permanently set during registration for regulatory compliance. If you need a different account type, you can create a separate account with a different email and phone number.",
      },
      {
        q: "How do I reset my password?",
        a: "Go to the login page and tap 'Forgot Password'. Enter your registered email address and we'll send a reset link valid for 24 hours.",
      },
      {
        q: "Is my data secure?",
        a: "Yes. We use bank-grade encryption (SSL/TLS) for all data in transit and at rest. Your personal information is stored securely and never shared with third parties without your consent.",
      },
    ],
  },
  {
    id: "wallet",
    label: "Wallet & Transactions",
    icon: WalletOutline,
    color: "#2e7d32",
    faqs: [
      {
        q: "How do I deposit money?",
        a: "Go to Wallet, tap 'Top Up', enter your mobile money number and amount. You'll receive a prompt on your phone — approve it and the funds appear in your wallet within seconds. We support MTN Mobile Money and Airtel Money.",
      },
      {
        q: "How long do withdrawals take?",
        a: "Withdrawals to mobile money typically process within 1-2 minutes. In rare cases during high traffic, it may take up to 30 minutes.",
      },
      {
        q: "What are the transaction limits?",
        a: "Minimum transaction: UGX 500. For wallet-to-wallet transfers: minimum UGX 10,000, maximum UGX 10,000,000.",
      },
      {
        q: "Are there any fees?",
        a: "Wallet-to-wallet transfers within Everpesa are free. Mobile money deposits and withdrawals may incur standard telecom charges. No hidden account fees.",
      },
    ],
  },
  {
    id: "investing",
    label: "Investing",
    icon: SparklesOutline,
    color: "#e65100",
    faqs: [
      {
        q: "How does P2P investing work?",
        a: "Approved borrower loans are listed on the platform. You buy 'units' at UGX 1,000 each. When the borrower repays, you receive your principal plus interest, distributed proportionally.",
      },
      {
        q: "What returns can I expect?",
        a: "P2P lending returns range from 10-20% depending on loan duration and risk tier. Unit trust funds range from 8-22% p.a. Past performance does not guarantee future results.",
      },
      {
        q: "Is my investment guaranteed?",
        a: "P2P investments carry risk — if a borrower defaults, you may lose some or all of your investment. We mitigate this through KYC checks and credit scoring, but no investment is risk-free.",
      },
    ],
  },
  {
    id: "loans",
    label: "Loans & Borrowing",
    icon: DocumentTextOutline,
    color: "#6a1b9a",
    faqs: [
      {
        q: "How do I apply for a loan?",
        a: "Go to your dashboard and tap 'Apply Loan'. Fill in the amount, duration, purpose, and repayment method. You'll be notified of the decision, typically within 1-2 business days.",
      },
      {
        q: "What loan amounts are available?",
        a: "Business cash loans range from UGX 10,000 to UGX 10,000,000. Durations: 1 week to 3 months. Interest rates: 10-20% depending on duration.",
      },
      {
        q: "What is BNPL (device financing)?",
        a: "Purchase phones and devices on installment plans (3, 6, or 12 months). Pay a deposit upfront, then monthly repayments. The device remains Everpesa's property until fully paid.",
      },
      {
        q: "What happens if I miss a payment?",
        a: "Late payments incur penalties as outlined in your loan offer. Repeated missed payments lead to default classification, affecting your credit score. Contact us early if you're struggling — we can discuss restructuring.",
      },
    ],
  },
];

// ─── Contact Data ───────────────────────────────────────────────
const CONTACTS = [
  {
    icon: MailOutline,
    label: "Email Us",
    detail: "info@everpesa.com",
    action: "mailto:info@everpesa.com",
    color: PRIMARY_COLOR,
    desc: "We respond within 24 hours",
  },
  {
    icon: CallOutline,
    label: "Call Us",
    detail: "+256 786 493 685",
    action: "tel:+256786493685",
    color: "#e65100",
    desc: "Mon–Fri, 9am–5pm EAT",
  },
  {
    icon: CallOutline,
    label: "Call Us",
    detail: "+256 704 476 881",
    action: "tel:+256704476881",
    color: "#e65100",
    desc: "Mon–Fri, 9am–5pm EAT",
  },
];

// ─── Component ──────────────────────────────────────────────────
export const HelpCenterPage = () => {
  useTitle("Help Center");
  const [search, setSearch] = useState("");
  const [openCategory, setOpenCategory] = useState<string | null>(null);
  const [openFaq, setOpenFaq] = useState<string | null>(null);

  const filteredCategories = search.trim()
    ? FAQ_CATEGORIES.map((cat) => ({
        ...cat,
        faqs: cat.faqs.filter(
          (f) =>
            f.q.toLowerCase().includes(search.toLowerCase()) ||
            f.a.toLowerCase().includes(search.toLowerCase())
        ),
      })).filter((cat) => cat.faqs.length > 0)
    : FAQ_CATEGORIES;

  const totalResults = filteredCategories.reduce((sum, cat) => sum + cat.faqs.length, 0);

  return (
    <PageWrapper>
      <AnimateChildren>
        {/* Header */}
        <HeaderSection>
          <HelpCircleOutline fontSize={36} color="#fff" />
          <div className="font20 bold white mt5">How can we help?</div>
          <div className="font13 white" style={{ opacity: 0.85 }}>
            Search our FAQ or reach out to our team
          </div>
          <SearchBox>
            <SearchOutline fontSize={16} color="#999" />
            <SearchInput
              value={search}
              onChange={(e) => { setSearch(e.target.value); setOpenCategory(null); }}
              placeholder="Search for answers..."
            />
          </SearchBox>
          {search && (
            <div className="font12 white mt5" style={{ opacity: 0.7 }}>
              {totalResults} result{totalResults !== 1 ? "s" : ""} found
            </div>
          )}
        </HeaderSection>

        {/* Contact Support */}
        <div style={{ padding: "20px 15px" }} className="flexColumn gap15">
          <Heading className="font14">Contact Support</Heading>

          {CONTACTS.map((c, i) => (
            <ContactCard
              key={i}
              onClick={() => window.open(c.action, "_blank")}
              className="pointer animate"
            >
              <IconCircle style={{ background: alpha(c.color, 0.08), color: c.color }}>
                <c.icon fontSize={18} />
              </IconCircle>
              <div className="flexGrow" style={{ minWidth: 0 }}>
                <div className="font13 semiBold textDark">{c.label}</div>
                <div className="font12 main">{c.detail}</div>
              </div>
              <div className="font10 grey textEnd" style={{ maxWidth: 130, lineHeight: 1.4 }}>
                {c.desc}
              </div>
            </ContactCard>
          ))}
        </div>

        {/* AI Advisor */}
        <div style={{ padding: "8px 15px 20px" }}>
          <Link to="/advisor">
            <AdvisorCard className="r15 p20 flexNullCenter gap15 pointer animate">
              <AdvisorIcon>
                <SparklesOutline fontSize={22} />
              </AdvisorIcon>
              <div className="flexGrow" style={{ minWidth: 0 }}>
                <div className="font14 semiBold white">Ask our AI Advisor</div>
                <div className="font12 white" style={{ opacity: 0.8, lineHeight: 1.5, marginTop: 4 }}>
                  Get instant answers about investments, loans, and your account
                </div>
              </div>
              <ChevronForward fontSize={20} color="rgba(255,255,255,0.7)" />
            </AdvisorCard>
          </Link>
        </div>

        {/* FAQs */}
        <div style={{ padding: "0 15px" }} className="flexColumn gap15">
          <Heading className="font14">
            {search ? "Search Results" : "Frequently Asked Questions"}
          </Heading>

          {filteredCategories.map((cat) => {
            const isOpen = openCategory === cat.id || !!search;
            return (
              <CategoryCard key={cat.id} className="r15 whiteBg hidden">
                <CategoryHeader
                  onClick={() => setOpenCategory(isOpen && !search ? null : cat.id)}
                  className="pointer"
                >
                  <IconCircle style={{ background: alpha(cat.color, 0.08), color: cat.color, width: 38, height: 38 }}>
                    <cat.icon fontSize={16} />
                  </IconCircle>
                  <div className="flexGrow">
                    <div className="font13 semiBold textDark">{cat.label}</div>
                    <div className="font11 grey">{cat.faqs.length} questions</div>
                  </div>
                  <ChevronDown
                    fontSize={16}
                    className="grey"
                    style={{ transform: isOpen ? "rotate(180deg)" : "rotate(0deg)", transition: "transform 0.2s" }}
                  />
                </CategoryHeader>

                {isOpen && (
                  <FaqList>
                    {cat.faqs.map((faq, i) => {
                      const faqKey = `${cat.id}-${i}`;
                      const isFaqOpen = openFaq === faqKey;
                      return (
                        <FaqItem key={i}>
                          <FaqQuestion onClick={() => setOpenFaq(isFaqOpen ? null : faqKey)} className="pointer">
                            <HelpCircleOutline fontSize={15} color={cat.color} style={{ flexShrink: 0, marginTop: 1 }} />
                            <div className="font13 textDark flexGrow">{faq.q}</div>
                            <ChevronDown
                              fontSize={14}
                              className="grey"
                              style={{ transform: isFaqOpen ? "rotate(180deg)" : "rotate(0deg)", transition: "transform 0.2s", flexShrink: 0 }}
                            />
                          </FaqQuestion>
                          {isFaqOpen && <FaqAnswer>{faq.a}</FaqAnswer>}
                        </FaqItem>
                      );
                    })}
                  </FaqList>
                )}
              </CategoryCard>
            );
          })}
        </div>

        {/* Operating Hours */}
        <div style={{ padding: "20px 15px 30px" }}>
          <HoursCard className="r15 p18 flexNullCenter gap14">
            <IconCircle style={{ background: primary10, color: PRIMARY_COLOR }}>
              <TimeOutline fontSize={18} />
            </IconCircle>
            <div>
              <div className="font13 semiBold textDark mb5">Support Hours</div>
              <div className="font12 grey" style={{ lineHeight: 1.6 }}>
                Phone: Mon–Fri, 9:00 AM – 5:00 PM EAT<br />
                Email: 24/7 (response within 24 hours)
              </div>
            </div>
          </HoursCard>
        </div>
      </AnimateChildren>
    </PageWrapper>
  );
};

// ─── Styles ─────────────────────────────────────────────────────
const PageWrapper = styled.div`
  max-width: 600px;
`;

const HeaderSection = styled.div`
  background: linear-gradient(135deg, ${PRIMARY_COLOR} 0%, ${alpha(PRIMARY_COLOR, 0.75)} 100%);
  padding: 30px 20px 24px;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 4px;
`;

const SearchBox = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  max-width: 300px;
  margin-top: 16px;
  padding: 14px 18px;
  background: rgba(255, 255, 255, 0.95);
  border-radius: 15px;
`;

const SearchInput = styled.input`
  flex: 1;
  border: none;
  outline: none;
  font-size: 14px;
  color: #333;
  background: transparent;
  &::placeholder { color: #aaa; }
`;

const ContactCard = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 16px 18px;
  background: #fff;
  border-radius: 15px;
  border: 1px solid ${PRIMARY_LIGHT};

  &:hover {
    border-color: ${alpha(PRIMARY_COLOR, 0.2)};
    box-shadow: 0 2px 12px ${alpha(PRIMARY_COLOR, 0.04)};
  }
`;

const IconCircle = styled.div`
  width: 42px;
  height: 42px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

const AdvisorCard = styled.div`
  background: linear-gradient(135deg, ${PRIMARY_COLOR} 0%, ${alpha(PRIMARY_COLOR, 0.8)} 100%);
`;

const AdvisorIcon = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.15);
  color: #fff;
  flex-shrink: 0;
`;

const CategoryCard = styled.div`
  border: 1px solid ${PRIMARY_LIGHT};
`;

const CategoryHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px 18px;
`;

const FaqList = styled.div`
  border-top: 1px solid ${PRIMARY_LIGHT};
`;

const FaqItem = styled.div`
  border-bottom: 1px solid ${PRIMARY_LIGHT};
  &:last-child { border-bottom: none; }
`;

const FaqQuestion = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 14px 18px;
  &:hover { background: ${primary5}; }
`;

const FaqAnswer = styled.div`
  padding: 0 18px 16px 46px;
  font-size: 13px;
  color: #555;
  line-height: 1.7;
`;

const HoursCard = styled.div`
  background: #fff;
  border: 1px solid ${PRIMARY_LIGHT};
`;